import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { Loader2, MessageCircle, ChevronLeft, ChevronRight, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLastSync, formatSyncAge } from '@/hooks/useLastSync';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { usePostsTable, PostFilters } from '@/hooks/useTeamPosts';
import { useTeamPosters } from '@/hooks/useTeamPosters';
import { PostEngagementModal } from '@/components/team-posts/PostEngagementModal';
import { TeamPostsFilterSidebar } from '@/components/team-posts/TeamPostsFilterSidebar';
import { SocialPost } from '@/types/team-posts';

export default function TeamPosts() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null);
  const [filters, setFilters] = useState<PostFilters>({
    category: [],
    profile: [],
    dateRange: 'all',
  });
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const toggleSort = (col: string) => {
    if (sortColumn === col) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ col }: { col: string }) => {
    if (sortColumn !== col) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  // Sync freshness
  const { data: lastSync } = useLastSync('social');
  const syncAge = formatSyncAge(lastSync?.completed_at ?? null);

  // Fetch data
  const { data: postersData } = useTeamPosters();
  const { data: postsResult, isLoading } = usePostsTable(filters, search, page);

  // Derive unique categories and profiles for filter dropdowns
  const categories = useMemo(() => {
    if (!postersData) return [];
    return [...new Set(postersData.map(p => p.category).filter(Boolean))].sort();
  }, [postersData]);

  const profiles = useMemo(() => {
    if (!postersData) return [];
    return [...new Set(postersData.map(p => p.profile_name).filter(Boolean))].sort();
  }, [postersData]);


  const rawPosts = postsResult?.data || [];
  const posts = useMemo(() => {
    if (!sortColumn) return rawPosts;
    return [...rawPosts].sort((a, b) => {
      const aVal = (a as any)[sortColumn] ?? '';
      const bVal = (b as any)[sortColumn] ?? '';
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rawPosts, sortColumn, sortDirection]);
  const totalCount = postsResult?.count || 0;
  const pageSize = postsResult?.pageSize || 50;
  const totalPages = Math.ceil(totalCount / pageSize);

  const hasActiveFilters = (filters.category?.length || 0) > 0 || (filters.profile?.length || 0) > 0 || filters.dateRange !== 'all';

  const clearAllFilters = () => {
    setFilters({ category: [], profile: [], dateRange: 'all' });
    setSearch('');
    setPage(1);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Social Listening - Contacts</h1>
          <Badge variant={syncAge.isStale ? "destructive" : "secondary"} className="text-xs flex items-center gap-1">
            <RefreshCw className="h-3 w-3" />
            Synced {syncAge.label}
          </Badge>
        </div>
        <p className="text-muted-foreground">Track social engagement from team members, thought leaders, and competitors</p>
      </div>

      {/* Grid layout: sidebar + content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left: Filter Sidebar */}
        <div className="lg:col-span-1">
          <TeamPostsFilterSidebar
            search={search}
            onSearchChange={(v) => { setSearch(v); setPage(1); }}
            selectedProfile={filters.profile?.[0] || 'all'}
            onProfileChange={(v) => {
              setFilters(prev => ({ ...prev, profile: v === 'all' ? [] : [v] }));
              setPage(1);
            }}
            selectedCategory={filters.category?.[0] || 'all'}
            onCategoryChange={(v) => {
              setFilters(prev => ({ ...prev, category: v === 'all' ? [] : [v] }));
              setPage(1);
            }}
            dateRange={filters.dateRange || 'all'}
            onDateRangeChange={(v) => {
              setFilters(prev => ({ ...prev, dateRange: v }));
              setPage(1);
            }}
            profiles={profiles}
            categories={categories}
            onClearAll={clearAllFilters}
            hasActiveFilters={hasActiveFilters}
            totalCount={totalCount}
          />
        </div>

        {/* Right: Content */}
        <div className="lg:col-span-3 space-y-6">

          {/* Table */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground border rounded-lg">
              <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No social posts found</p>
              <p className="text-xs mt-1">Posts will appear here once synced from the social listening pipeline</p>
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[110px] cursor-pointer select-none" onClick={() => toggleSort('post_date')}>
                      <span className="inline-flex items-center">Date<SortIcon col="post_date" /></span>
                    </TableHead>
                    <TableHead className="w-[140px]">Poster</TableHead>
                    <TableHead className="min-w-[300px]">Content</TableHead>
                    <TableHead className="w-[80px] text-center cursor-pointer select-none" onClick={() => toggleSort('likes')}>
                      <span className="inline-flex items-center justify-center">Likes<SortIcon col="likes" /></span>
                    </TableHead>
                    <TableHead className="w-[80px] text-center cursor-pointer select-none" onClick={() => toggleSort('comments')}>
                      <span className="inline-flex items-center justify-center">Comments<SortIcon col="comments" /></span>
                    </TableHead>
                    <TableHead className="w-[100px] text-center cursor-pointer select-none" onClick={() => toggleSort('unique_engagers')}>
                      <span className="inline-flex items-center justify-center">Engagers<SortIcon col="unique_engagers" /></span>
                    </TableHead>
                    <TableHead className="w-[80px] text-center cursor-pointer select-none" onClick={() => toggleSort('top_account_engagers')}>
                      <span className="inline-flex items-center justify-center">Top Accts<SortIcon col="top_account_engagers" /></span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posts.map((post) => {
                    const formattedDate = post.post_date
                      ? format(new Date(post.post_date), 'MMM d, yyyy')
                      : '-';

                    return (
                      <TableRow
                        key={post.post_id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setSelectedPost(post)}
                      >
                        <TableCell>
                          <span className="text-sm text-muted-foreground">{formattedDate}</span>
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="text-sm font-medium">{post.poster_name || 'Unknown'}</span>
                            {post.category && (
                              <Badge variant="outline" className="text-[10px] ml-1">{post.category}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm line-clamp-2">
                            {post.content_preview || post.full_content || '-'}
                          </p>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-sm">{post.likes}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-sm">{post.comments}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-sm font-medium">{post.unique_engagers}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          {post.top_account_engagers > 0 ? (
                            <Badge className="text-xs bg-amber-100 text-amber-800">
                              {post.top_account_engagers}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Post Detail Modal */}
      <PostEngagementModal
        post={selectedPost}
        open={!!selectedPost}
        onOpenChange={(open) => !open && setSelectedPost(null)}
      />
    </div>
  );
}
